import {
  type ChangeEvent,
  type ReactNode,
  type Ref,
  useMemo,
  useState,
} from 'react';
import { Input } from '@mantine/core';
import { useDidUpdate } from '@mantine/hooks';
import clsx from 'clsx';

import { useFormattingLocales } from '../../utils/getFormattingLocales';
import { ClearButton } from '../clearbutton/ClearButton';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';

import { getDateStringParser } from './helper/dateStringParser';
import { isDateValid } from './helper/isDateValid';

import inputStyles from '../input/input.module.scss';
import calendarStyles from './calendar.module.scss';

export type DateInputProps = {
  className?: string;
  disabled: boolean;
  readOnly?: boolean;
  clearable?: boolean;
  icon?: string;
  id?: string;
  value: Date | null | undefined;
  focused?: boolean;
  loading?: boolean;
  onClear: () => void;
  onFocus: () => void;
  placeholder?: string;
  onChange: (date: Date) => void;
  dateTimeFormatOptions?: Intl.DateTimeFormatOptions;
  ref?: Ref<HTMLInputElement>;
};

export function DateInput({
  className,
  disabled,
  readOnly = false,
  clearable,
  focused = false,
  loading,
  icon,
  ref,
  value,
  onClear,
  onFocus,
  onChange,
  dateTimeFormatOptions,
  ...props
}: DateInputProps) {
  const locales = useFormattingLocales();
  const dateTimeFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(
        locales,
        dateTimeFormatOptions ?? {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }
      ),
    [dateTimeFormatOptions, locales]
  );

  const parser = useMemo(() => {
    try {
      return getDateStringParser(dateTimeFormat);
    } catch {
      return null;
    }
  }, [dateTimeFormat]);

  const [inputValue, setInputValue] = useState<string>(
    value ? dateTimeFormat.format(value) : ''
  );
  const showClearAction = value && !loading && !disabled && !readOnly;

  let rightSection: ReactNode | undefined;
  if (loading) {
    rightSection = <LoadingIndicator />;
  } else if (clearable) {
    rightSection = (
      <ClearButton
        hidden={!showClearAction}
        disabled={disabled}
        onClear={onClear}
      />
    );
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    if (!parser) {
      return;
    }

    const val = event.currentTarget.value;
    setInputValue(val);

    if (val.trim() === '' && clearable) {
      onClear();
    } else {
      const dateValue = parser(val);
      if (isDateValid(dateValue)) {
        onChange(dateValue);
      }
    }
  }

  function handleInputBlur() {
    setInputValue(value ? dateTimeFormat.format(value) : '');
  }

  useDidUpdate(() => {
    if (!focused) {
      setInputValue(value ? dateTimeFormat.format(value) : '');
    }
  }, [value, dateTimeFormat, focused]);

  return (
    <Input
      readOnly={readOnly}
      autoComplete="off"
      unstyled
      ref={ref}
      disabled={disabled || loading}
      classNames={{
        input: clsx(inputStyles['input'], className),
        section: inputStyles['section'],
        wrapper: inputStyles['wrapper'],
      }}
      leftSection={
        icon && (
          <GoogleFontIcon
            name={icon}
            solid
            className={clsx(calendarStyles['icon'], {
              [calendarStyles['icon--disabled']]: disabled,
              [calendarStyles['icon--focused']]: focused,
              [calendarStyles['icon--loading']]: loading,
            })}
          />
        )
      }
      rightSection={rightSection}
      type="text"
      onChange={handleInputChange}
      onFocus={onFocus}
      onBlur={handleInputBlur}
      value={inputValue}
      {...props}
      id={props.id}
    />
  );
}
